import classNames from 'classnames'
import { type FC } from 'react'
import { Link } from 'react-router-dom'
import { ArchiveReason, type ArchivedBooking, type BookingClient, type Location, type SchedulingEventType } from '../../../models/Scheduling'
import { formatDateAndTime } from '../bookingFormat'
import { BookingRow } from '../rows/BookingRow'
import { ContactChips } from '../rows/ContactChips'
import { EventTypeChip } from '../rows/EventTypeChip'
import { ProviderBadge } from '../rows/ProviderBadge'
import { getMeetingPrepIds, pastMeetingPath } from '../shared/bookingNavigation'
import { getCancellationAuthor, getHistoryAttribution, getHistoryTag, getHistoryTimeLine, resolveActorName, type HistoryAttribution } from './historyDisplay'

interface HistoryRowProps {
  booking: ArchivedBooking
  eventType?: SchedulingEventType
  location?: Location
  currentUserId: string
  resolveUserName: (userId: string) => string | undefined
}

interface AttributionLineProps {
  attribution: HistoryAttribution
  clients: BookingClient[]
  currentUserId: string
  resolveUserName: (userId: string) => string | undefined
}

// The bold "{verb} by {name}" attribution line; renders nothing when the closing action is
// unattributed (legacy rows, calendar-sync cancels, unresolved actors).
const AttributionLine: FC<AttributionLineProps> = (props) => {
  const name = props.attribution.verb ? resolveActorName(props.attribution.actor, props) : undefined
  return props.attribution.verb && name ? <div className='bold zocks-text-gray-900'>{`${props.attribution.verb} by ${name}`}</div> : null
}

export const HistoryRow: FC<HistoryRowProps> = ({ booking, eventType, location, currentUserId, resolveUserName }) => {
  const tag = getHistoryTag(booking)
  const timeLine = getHistoryTimeLine(booking)
  const attribution = getHistoryAttribution(booking)
  const prepIds = booking.archiveReason === ArchiveReason.MEETING_TIME_PASSED ? getMeetingPrepIds(booking) : undefined
  const cancellationAuthor = getCancellationAuthor(attribution, { clients: booking.clients, currentUserId, resolveUserName })

  return (
    <BookingRow
      booking={booking}
      location={location}
      detailsVariant='history'
      detailsCancellationAuthor={cancellationAuthor}
      timeBlock={
        <div className='flex-1'>
          <span className={classNames('d-inline-block paragraph-small rounded-3 px-2 py-1 text-nowrap', tag.className)}>{tag.label}</span>
        </div>
      }
      summary={
        <div className='d-flex flex-column flex-4 mw-0 zocks-gap-0.25'>
          <div className='bold text-truncate'>{booking.title ?? eventType?.name}</div>
          <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
            <ContactChips clients={booking.clients} markGuests />
            {eventType ? (
              <>
                <span className='zocks-text-gray-600'>·</span>
                <EventTypeChip eventType={eventType} />
              </>
            ) : null}
            {booking.archiveReason === ArchiveReason.DISMISSED ? null : <ProviderBadge meetingMode={booking.meetingMode} meetingUrl={booking.meetingUrl} />}
          </div>
          {timeLine ? (
            <div className='d-flex align-items-center flex-wrap zocks-gap-0.25 paragraph-small zocks-text-gray-500'>
              {timeLine.kind === 'reschedule' ? (
                <>
                  <span>was {timeLine.was}</span>
                  <i className='bi bi-arrow-repeat' aria-hidden='true' />
                  <span>proposed {timeLine.proposed}</span>
                </>
              ) : (
                <span>{timeLine.prefix ? `${timeLine.prefix} ${timeLine.text}` : timeLine.text}</span>
              )}
            </div>
          ) : null}
        </div>
      }
    >
      <div className='d-flex flex-column align-items-end text-end paragraph-small zocks-gap-0.125'>
        {attribution.requestedOn ? <div className='zocks-text-gray-600'>Requested on {formatDateAndTime(new Date(attribution.requestedOn))}</div> : null}
        <AttributionLine attribution={attribution} clients={booking.clients} currentUserId={currentUserId} resolveUserName={resolveUserName} />
        {attribution.noResponse ? (
          <div className='zocks-text-gray-600'>No response from client</div>
        ) : prepIds ? (
          <Link to={pastMeetingPath(prepIds.roomId, prepIds.sessionId)} className='text-decoration-underline'>
            Now in Past Meetings
          </Link>
        ) : (
          <div className='zocks-text-gray-600'>{formatDateAndTime(new Date(attribution.closedAt))}</div>
        )}
      </div>
    </BookingRow>
  )
}
